import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastMessage, ToastContainer } from '../components/Toast';

/**
 * Generates a unique ID for toast messages
 */
function generateToastId(): string {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * Toast context value interface providing methods to show toast notifications
 */
interface ToastContextValue {
  /** Show a toast with custom type */
  addToast: (message: string, type?: ToastMessage['type'], duration?: number) => string;
  /** Remove a specific toast by ID */
  removeToast: (id: string) => void;
  /** Show a success toast (green) */
  showSuccess: (message: string, duration?: number) => string;
  /** Show an error toast (red) */
  showError: (message: string, duration?: number) => string;
  /** Show a warning toast (yellow) */
  showWarning: (message: string, duration?: number) => string;
  /** Show an info toast (blue) */
  showInfo: (message: string, duration?: number) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Props for the ToastProvider component
 */
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider - Provides global toast notification functionality
 *
 * Wrap your app with this provider to enable toast notifications anywhere:
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 *
 * Then use the useToastContext hook in any component:
 * ```tsx
 * const { showSuccess, showError } = useToastContext();
 * showSuccess('Task completed!');
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((message: string, type: ToastMessage['type'] = 'info', duration?: number) => {
    const id = generateToastId();
    const toast: ToastMessage = {
      id,
      message,
      type,
      duration,
    };

    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, duration?: number) => {
    return addToast(message, 'success', duration);
  }, [addToast]);

  const showError = useCallback((message: string, duration?: number) => {
    return addToast(message, 'error', duration);
  }, [addToast]);

  const showWarning = useCallback((message: string, duration?: number) => {
    return addToast(message, 'warning', duration);
  }, [addToast]);

  const showInfo = useCallback((message: string, duration?: number) => {
    return addToast(message, 'info', duration);
  }, [addToast]);

  const value: ToastContextValue = {
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Hook to access toast notification functions
 *
 * @throws Error if used outside of ToastProvider
 * @returns Toast context value with methods to show notifications
 *
 * @example
 * ```tsx
 * const { showSuccess, showError, showWarning, showInfo } = useToastContext();
 *
 * // Show different types of toasts
 * showSuccess('Item saved!');
 * showError('Failed to save');
 * showWarning('Unsaved changes', 5000); // 5 second duration
 * showInfo('Tip: Press Ctrl+S to save');
 * ```
 */
export function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}
